import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlusIcon, MailIcon, LockIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { useAuth } from '../../contexts/AuthContext';

const Signup: React.FC = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { signup, loading } = useAuth();
  const navigate = useNavigate();
  
  // Get selected plan from navigation state or localStorage
  const selectedPlan = location.state?.selectedPlan || localStorage.getItem('selectedPlan');

  const passwordRequirements = [
    { check: (p: string) => p.length >= 6, text: 'At least 6 characters' },
    { check: (p: string) => /[A-Z]/.test(p), text: 'One uppercase letter' },
    { check: (p: string) => /[0-9]/.test(p), text: 'One number' },
  ];

  const getPasswordStrength = () => {
    const passedRequirements = passwordRequirements.filter(req => req.check(password));
    return passedRequirements.length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    
    const failedRequirements = passwordRequirements.filter(req => !req.check(password));
    if (failedRequirements.length > 0) {
      return setError(`Password requirements not met: ${failedRequirements.map(r => r.text).join(', ')}`);
    }
    
    try {
      setError('');
      await signup(email, password);
      
      // Wait briefly to ensure database operations complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccess(true);
      
      // If user came from plan selection, redirect to subscription after signup
      if (selectedPlan) {
        localStorage.removeItem('selectedPlan'); // Clean up
        setTimeout(() => navigate('/subscription', { replace: true }), 2000);
      } else {
        setTimeout(() => navigate('/app'), 2000);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create an account';
      setError(message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 to-primary-700 flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="w-full max-w-md"
      >
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-white/10 p-3 rounded-full">
              <UserPlusIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-white/70">
            {selectedPlan ? `Join VoiceVision with the ${selectedPlan} plan` : 'Join VoiceVision today'}
          </p>
        </div>

        {/* Form Container */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-white/20">
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-success-500/20 border border-success-500/30 text-success-100 p-4 mb-6 rounded-lg backdrop-blur-sm"
            >
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                <p className="text-sm">
                  Account created successfully! 
                  {selectedPlan ? ' Redirecting to complete your subscription...' : ' Redirecting...'}
                </p>
              </div>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-error-500/20 border border-error-500/30 text-error-100 p-4 mb-6 rounded-lg backdrop-blur-sm"
            >
              <div className="flex items-center">
                <AlertCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                Email Address
               <span className="text-error-300 ml-1" aria-label="required">*</span>
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoComplete="email"
                leftIcon={<MailIcon className="h-5 w-5 text-gray-400" />}
                className="bg-white/90 border-white/20 text-gray-900 placeholder-gray-500 focus:bg-white focus:border-primary-500"
                fullWidth
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Password
               <span className="text-error-300 ml-1" aria-label="required">*</span>
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                autoComplete="new-password"
                leftIcon={<LockIcon className="h-5 w-5 text-gray-400" />}
                className="bg-white/90 border-white/20 text-gray-900 placeholder-gray-500 focus:bg-white focus:border-primary-500"
                fullWidth
              />
              
              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-3">
                  <div className="flex space-x-1 mb-2">
                    {[1, 2, 3].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          getPasswordStrength() >= level
                            ? getPasswordStrength() === 1
                              ? 'bg-error-400'
                              : getPasswordStrength() === 2
                              ? 'bg-warning-400'
                              : 'bg-success-400'
                            : 'bg-white/20'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="space-y-1">
                    {passwordRequirements.map((req, index) => (
                      <div key={index} className="flex items-center text-xs">
                        <div
                          className={`w-2 h-2 rounded-full mr-2 ${
                            req.check(password) ? 'bg-success-400' : 'bg-white/30'
                          }`}
                        />
                        <span className={req.check(password) ? 'text-success-200' : 'text-white/60'}>
                          {req.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-white mb-2">
                Confirm Password
               <span className="text-error-300 ml-1" aria-label="required">*</span>
              </label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                autoComplete="new-password"
                leftIcon={<LockIcon className="h-5 w-5 text-gray-400" />}
                className="bg-white/90 border-white/20 text-gray-900 placeholder-gray-500 focus:bg-white focus:border-primary-500"
                fullWidth
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-2 text-sm text-error-300">Passwords do not match</p>
              )}
            </div>

            <div className="flex items-start">
              <input
                id="agree-terms"
               aria-describedby="terms-description"
                name="agree-terms"
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                required
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded bg-white/90 mt-0.5"
              />
              <label htmlFor="agree-terms" className="ml-3 text-sm text-white/80 leading-5">
               <span id="terms-description">
                I agree to the{' '}
                <Link to="/terms" className="font-medium text-primary-300 hover:text-white transition-colors">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="font-medium text-primary-300 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
               </span>
              </label>
            </div>

            <Button
              type="submit"
             aria-label={loading ? 'Creating account, please wait' : 'Create your account'}
              variant="primary"
              fullWidth
              isLoading={loading}
              leftIcon={<UserPlusIcon className="h-5 w-5" />}
              className="bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-medium py-3 shadow-lg hover:shadow-xl transition-all duration-200"
              disabled={!agreedToTerms}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Sign In Link */}
          <div className="mt-8 text-center">
            <p className="text-white/70">
              Already have an account?{' '}
              <Link 
               aria-label="Sign in to your existing account"
                to="/login" 
                className="font-medium text-primary-300 hover:text-white transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} VoiceVision. All rights reserved.
          </p>
          <div className="flex justify-center space-x-6 mt-4">
            <Link to="/privacy" className="text-white/50 hover:text-white/70 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/50 hover:text-white/70 text-sm transition-colors">
              Terms of Service
            </Link>
            <a href="mailto:support@aichaperones.com" className="text-white/50 hover:text-white/70 text-sm transition-colors">
              Contact
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Signup;